import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import MdlAddBoardMedium from "../dialog/MdlAddBoardMedium";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";

const ACCENT = COLORS.primary_color_shade6 || "#039BE5";

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-IN");
}

function getClassStudentTotal(classId, studentCount) {
  return (studentCount || [])
    .filter((row) => String(row.class_id) === String(classId))
    .reduce((sum, row) => sum + Number(row.total_student || 0), 0);
}

function buildBoardSummary(boards, classes, studentCount) {
  return (boards || []).map((board) => {
    const boardClasses = (classes || []).filter(
      (item) => String(item.board_id) === String(board.board_id)
    );

    const classRows = boardClasses.map((item) => ({
      class_id: item.class_id,
      class_name: item.class_name,
      medium_name: item.medium_name || "—",
      student_count: getClassStudentTotal(item.class_id, studentCount),
    }));

    const studentTotal = classRows.reduce(
      (sum, item) => sum + item.student_count,
      0
    );

    return {
      board_id: board.board_id,
      board_name: board.board_name,
      class_count: classRows.length,
      student_count: studentTotal,
      classes: classRows,
    };
  });
}

function buildMediumSummary(mediums, classes, studentCount) {
  return (mediums || []).map((medium) => {
    const mediumClasses = (classes || []).filter(
      (item) => String(item.medium_id) === String(medium.medium_id)
    );

    const classRows = mediumClasses.map((item) => ({
      class_id: item.class_id,
      class_name: item.class_name,
      board_name: item.board_name || "—",
      student_count: getClassStudentTotal(item.class_id, studentCount),
    }));

    const studentTotal = classRows.reduce(
      (sum, item) => sum + item.student_count,
      0
    );

    return {
      medium_id: medium.medium_id,
      medium_name: medium.medium_name,
      class_count: classRows.length,
      student_count: studentTotal,
      classes: classRows,
    };
  });
}

const BoardMediumSettings = () => {
  const history = useHistory();
  const [loggedIn] = useState(!!localStorage.getItem("token"));
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [boardList, setBoardList] = useState([]);
  const [mediumList, setMediumList] = useState([]);
  const [showAddBoard, setShowAddBoard] = useState(false);
  const [showAddMedium, setShowAddMedium] = useState(false);

  useEffect(() => {
    const mSession = SessionManager.getCurrentSession();
    if (mSession?.[0]) {
      setSessionId(mSession[0].session_id || "");
      setSessionName(mSession[0].session_name || "");
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!sessionId) return;

    setLoading(true);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      const url =
        process.env.REACT_APP_SERVER_NAME +
        process.env.REACT_APP_TARGET_DIR +
        "getClassWithStudentCount.php";

      const response = await axios.get(url, {
        params: {
          school_code: schoolCode,
          session_id: sessionId,
        },
      });

      if (response.data?.api_status === "OK") {
        const payload = response.data.data || {};
        const apiBoard = Array.isArray(payload.board) ? payload.board : [];
        const apiMedium = Array.isArray(payload.medium) ? payload.medium : [];
        const apiClass = Array.isArray(payload.class) ? payload.class : [];
        const apiStudentCount = Array.isArray(payload.student_count)
          ? payload.student_count
          : [];

        setBoardList(buildBoardSummary(apiBoard, apiClass, apiStudentCount));
        setMediumList(buildMediumSummary(apiMedium, apiClass, apiStudentCount));
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Could not load board and medium data."
        );
      }
    } catch (error) {
      console.error("BoardMediumSettings fetch error:", error);
      myToast.notify_error("An error occurred while fetching board and medium data.");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRemove = useCallback(
    async (type, item) => {
      const isBoard = type === "board";
      const name = isBoard ? item.board_name : item.medium_name;

      if (
        !window.confirm(
          `Remove ${isBoard ? "board" : "medium"} "${name}"? This cannot be undone.`
        )
      ) {
        return;
      }

      setLoading(true);
      try {
        const schoolCode = SessionManager.getSchoolCode();
        const url =
          process.env.REACT_APP_SERVER_NAME +
          process.env.REACT_APP_TARGET_DIR +
          (isBoard ? "removeBoard.php" : "removeMedium.php");

        const response = await axios.get(url, {
          params: {
            school_code: schoolCode,
            session_id: sessionId,
            ...(isBoard
              ? { board_id: item.board_id }
              : { medium_id: item.medium_id }),
          },
        });

        if (response.data?.api_status === "OK") {
          myToast.notify_success(
            response.data.api_remark ||
              `${isBoard ? "Board" : "Medium"} removed successfully.`
          );
          await fetchData();
        } else {
          myToast.notify_warning(
            response.data?.api_remark ||
              `Could not remove ${isBoard ? "board" : "medium"}.`
          );
          setLoading(false);
        }
      } catch (error) {
        console.error(`Remove ${type} error:`, error);
        myToast.notify_error(`An error occurred while removing ${type}.`);
        setLoading(false);
      }
    },
    [sessionId, fetchData]
  );

  const totals = useMemo(() => {
    const totalClasses = boardList.reduce(
      (sum, item) => sum + Number(item.class_count || 0),
      0
    );
    const totalStudents = boardList.reduce(
      (sum, item) => sum + Number(item.student_count || 0),
      0
    );
    return {
      boards: boardList.length,
      mediums: mediumList.length,
      classes: totalClasses,
      students: totalStudents,
    };
  }, [boardList, mediumList]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div className="content" style={{ background: COLORS.white }}>
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <a href="#">Board &amp; Medium Settings</a>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton onClick={() => history.goBack()} />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow bms-page"
        style={{ "--bms-accent": ACCENT, "--mMargin": "50px" }}
      >
        <div className="bms-hero">
          <div className="bms-hero-main">
            <span className="bms-hero-icon" aria-hidden="true">
              <i className="fa fa-university" />
            </span>
            <div>
              <h2 className="bms-heading">Board &amp; Medium</h2>
              <p className="bms-subheading">
                Session: <strong>{sessionName || "—"}</strong>
              </p>
            </div>
          </div>

          <div className="bms-hero-stats">
            <div className="bms-hero-stat">
              <div className="bms-hero-stat-label">Boards</div>
              <div className="bms-hero-stat-value">
                {loading ? "…" : formatCount(totals.boards)}
              </div>
            </div>
            <div className="bms-hero-stat">
              <div className="bms-hero-stat-label">Mediums</div>
              <div className="bms-hero-stat-value">
                {loading ? "…" : formatCount(totals.mediums)}
              </div>
            </div>
            <div className="bms-hero-stat">
              <div className="bms-hero-stat-label">Classes</div>
              <div className="bms-hero-stat-value">
                {loading ? "…" : formatCount(totals.classes)}
              </div>
            </div>
            <div className="bms-hero-stat">
              <div className="bms-hero-stat-label">Students</div>
              <div className="bms-hero-stat-value">
                {loading ? "…" : formatCount(totals.students)}
              </div>
            </div>
          </div>
        </div>

        {showAddBoard && (
          <MdlAddBoardMedium
            type="board"
            sessionId={sessionId}
            actionClose={() => setShowAddBoard(false)}
            action2={fetchData}
          />
        )}

        {showAddMedium && (
          <MdlAddBoardMedium
            type="medium"
            sessionId={sessionId}
            actionClose={() => setShowAddMedium(false)}
            action2={fetchData}
          />
        )}

        {loading ? (
          <div className="bms-loader-wrap">
            <MyLoader />
          </div>
        ) : (
          <div className="bms-grid">
            <section className="bms-section">
              <div className="bms-section-head bms-section-head--board">
                <i className="fa fa-university" aria-hidden="true" />
                <h3>Boards</h3>
                <span className="bms-section-count">{boardList.length}</span>
                <button
                  type="button"
                  className="bms-add-btn bms-add-btn--board"
                  onClick={() => setShowAddBoard(true)}
                >
                  <i className="fa fa-plus" aria-hidden="true" />
                  Add Board
                </button>
              </div>

              <div className="bms-list">
                {boardList.length === 0 ? (
                  <div className="bms-empty bms-empty--inline">
                    <div className="bms-empty-title">No boards yet</div>
                    <div className="bms-empty-text">
                      Add a board for this session.
                    </div>
                  </div>
                ) : null}
                {boardList.map((board) => (
                  <div className="bms-card bms-card--board" key={board.board_id}>
                    <div className="bms-card-head">
                      <div className="bms-card-title">{board.board_name}</div>
                      <div className="bms-card-metrics">
                        <span className="bms-chip bms-chip--class">
                          <i className="fa fa-graduation-cap" aria-hidden="true" />
                          {formatCount(board.class_count)} Classes
                        </span>
                        <span className="bms-chip bms-chip--student">
                          <i className="fa fa-users" aria-hidden="true" />
                          {formatCount(board.student_count)} Students
                        </span>
                      </div>
                    </div>

                    {board.classes.length > 0 ? (
                      <div className="bms-class-chips">
                        {board.classes.map((cls) => (
                          <span
                            className="bms-class-chip"
                            key={cls.class_id}
                            title={`${cls.medium_name} • ${formatCount(cls.student_count)} students`}
                          >
                            <i className="fa fa-graduation-cap" aria-hidden="true" />
                            {cls.class_name}
                            <span className="bms-class-chip-count">
                              {formatCount(cls.student_count)}
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="bms-no-classes">
                        <span>No classes linked</span>
                        <button
                          type="button"
                          className="bms-remove-btn"
                          onClick={() => handleRemove("board", board)}
                        >
                          <i className="fa fa-trash" aria-hidden="true" />
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <section className="bms-section">
              <div className="bms-section-head bms-section-head--medium">
                <i className="fa fa-language" aria-hidden="true" />
                <h3>Mediums</h3>
                <span className="bms-section-count">{mediumList.length}</span>
                <button
                  type="button"
                  className="bms-add-btn bms-add-btn--medium"
                  onClick={() => setShowAddMedium(true)}
                >
                  <i className="fa fa-plus" aria-hidden="true" />
                  Add Medium
                </button>
              </div>

              <div className="bms-list">
                {mediumList.length === 0 ? (
                  <div className="bms-empty bms-empty--inline">
                    <div className="bms-empty-title">No mediums yet</div>
                    <div className="bms-empty-text">
                      Add a medium for this session.
                    </div>
                  </div>
                ) : null}
                {mediumList.map((medium) => (
                  <div className="bms-card bms-card--medium" key={medium.medium_id}>
                    <div className="bms-card-head">
                      <div className="bms-card-title">{medium.medium_name}</div>
                      <div className="bms-card-metrics">
                        <span className="bms-chip bms-chip--class">
                          <i className="fa fa-graduation-cap" aria-hidden="true" />
                          {formatCount(medium.class_count)} Classes
                        </span>
                        <span className="bms-chip bms-chip--student">
                          <i className="fa fa-users" aria-hidden="true" />
                          {formatCount(medium.student_count)} Students
                        </span>
                      </div>
                    </div>

                    {medium.classes.length > 0 ? (
                      <div className="bms-class-chips">
                        {medium.classes.map((cls) => (
                          <span
                            className="bms-class-chip bms-class-chip--medium"
                            key={cls.class_id}
                            title={`${cls.board_name} • ${formatCount(cls.student_count)} students`}
                          >
                            <i className="fa fa-graduation-cap" aria-hidden="true" />
                            {cls.class_name}
                            <span className="bms-class-chip-count">
                              {formatCount(cls.student_count)}
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="bms-no-classes">
                        <span>No classes linked</span>
                        <button
                          type="button"
                          className="bms-remove-btn"
                          onClick={() => handleRemove("medium", medium)}
                        >
                          <i className="fa fa-trash" aria-hidden="true" />
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>

      <style>{`
        .bms-page {
          padding-bottom: 24px;
          background: #ffffff !important;
        }
        .bms-hero {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          flex-wrap: wrap;
          margin-bottom: 16px;
          padding: 13px 16px;
          border-radius: 13px;
          background: linear-gradient(135deg, var(--bms-accent) 0%, ${COLORS.primary_color_shade7 || "#0288D1"} 100%);
          color: #fff;
          box-shadow: 0 4px 16px color-mix(in srgb, var(--bms-accent) 35%, transparent);
        }
        .bms-hero-main {
          display: flex;
          align-items: center;
          gap: 11px;
          min-width: 0;
          flex: 1;
        }
        .bms-hero-icon {
          width: 40px;
          height: 40px;
          border-radius: 11px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
          flex-shrink: 0;
          color: #fff;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.28);
        }
        .bms-heading {
          margin: 0;
          font-size: 18px;
          font-weight: 800;
          line-height: 1.2;
          color: #fff;
        }
        .bms-subheading {
          margin: 2px 0 0;
          font-size: 12px;
          line-height: 1.25;
          color: rgba(255, 255, 255, 0.9);
        }
        .bms-hero-stats {
          display: flex;
          align-items: stretch;
          gap: 8px;
          flex-wrap: wrap;
        }
        .bms-hero-stat {
          text-align: center;
          padding: 6px 12px;
          border-radius: 11px;
          background: rgba(255, 255, 255, 0.14);
          border: 1px solid rgba(255, 255, 255, 0.24);
          min-width: 72px;
        }
        .bms-hero-stat-label {
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          opacity: 0.9;
        }
        .bms-hero-stat-value {
          font-size: 18px;
          font-weight: 800;
          line-height: 1.15;
          margin-top: 2px;
        }
        .bms-loader-wrap {
          display: flex;
          justify-content: center;
          padding: 48px 0;
        }
        .bms-empty {
          text-align: center;
          padding: 48px 20px;
          color: #6b7280;
        }
        .bms-empty-icon {
          width: 56px;
          height: 56px;
          margin: 0 auto 14px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }
        .bms-empty-title {
          font-size: 16px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 6px;
        }
        .bms-empty-text {
          font-size: 13px;
        }
        .bms-grid {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .bms-section-head {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          padding: 10px 12px;
          border-radius: 12px;
          font-weight: 800;
          color: #1e293b;
        }
        .bms-section-head h3 {
          margin: 0;
          font-size: 15px;
          font-weight: 800;
          flex: 1;
        }
        .bms-section-head--board {
          background: color-mix(in srgb, var(--bms-accent) 10%, #ffffff);
          border: 1px solid color-mix(in srgb, var(--bms-accent) 22%, #e5e7eb);
          color: var(--bms-accent);
        }
        .bms-section-head--medium {
          background: color-mix(in srgb, #14b8a6 10%, #ffffff);
          border: 1px solid color-mix(in srgb, #14b8a6 22%, #e5e7eb);
          color: #0d9488;
        }
        .bms-section-count {
          font-size: 11px;
          font-weight: 800;
          padding: 3px 9px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.75);
        }
        .bms-add-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 12px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          border: 1px solid transparent;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.15s ease, transform 0.15s ease;
        }
        .bms-add-btn:hover {
          transform: translateY(-1px);
        }
        .bms-add-btn--board {
          color: var(--bms-accent);
          background: #fff;
          border-color: color-mix(in srgb, var(--bms-accent) 30%, #e5e7eb);
        }
        .bms-add-btn--medium {
          color: #0d9488;
          background: #fff;
          border-color: color-mix(in srgb, #14b8a6 30%, #e5e7eb);
        }
        .bms-empty--inline {
          padding: 24px 16px;
          border-radius: 12px;
          border: 1px dashed #e5e7eb;
          background: #fafbfc;
        }
        .bms-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .bms-card {
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          overflow: hidden;
        }
        .bms-card--board {
          border-color: color-mix(in srgb, var(--bms-accent) 24%, #e5e7eb);
        }
        .bms-card--medium {
          border-color: color-mix(in srgb, #14b8a6 24%, #e5e7eb);
        }
        .bms-card-head {
          padding: 12px 14px;
          border-bottom: 1px solid #f1f5f9;
        }
        .bms-card-title {
          font-size: 16px;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 8px;
        }
        .bms-card-metrics {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .bms-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
        }
        .bms-chip--class {
          color: #7c3aed;
          background: color-mix(in srgb, #8b5cf6 12%, #ffffff);
          border: 1px solid color-mix(in srgb, #8b5cf6 22%, #e5e7eb);
        }
        .bms-chip--student {
          color: #2563eb;
          background: color-mix(in srgb, #3b82f6 12%, #ffffff);
          border: 1px solid color-mix(in srgb, #3b82f6 22%, #e5e7eb);
        }
        .bms-class-chips {
          padding: 10px 12px 12px;
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }
        .bms-class-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          color: #334155;
          background: color-mix(in srgb, var(--bms-accent) 8%, #ffffff);
          border: 1px solid color-mix(in srgb, var(--bms-accent) 22%, #e5e7eb);
          cursor: default;
        }
        .bms-class-chip i {
          font-size: 10px;
          color: var(--bms-accent);
        }
        .bms-class-chip--medium {
          background: color-mix(in srgb, #14b8a6 8%, #ffffff);
          border-color: color-mix(in srgb, #14b8a6 22%, #e5e7eb);
        }
        .bms-class-chip--medium i {
          color: #0d9488;
        }
        .bms-class-chip-count {
          font-size: 10px;
          font-weight: 800;
          padding: 1px 7px;
          border-radius: 999px;
          color: #2563eb;
          background: color-mix(in srgb, #3b82f6 12%, #ffffff);
          border: 1px solid color-mix(in srgb, #3b82f6 22%, #e5e7eb);
        }
        .bms-no-classes {
          padding: 12px 14px 14px;
          font-size: 12px;
          color: #94a3b8;
          font-style: italic;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
        }
        .bms-remove-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          font-style: normal;
          color: #dc2626;
          background: #fef2f2;
          border: 1px solid #fecaca;
          cursor: pointer;
          transition: background 0.15s ease, transform 0.15s ease;
        }
        .bms-remove-btn:hover {
          background: #fee2e2;
          transform: translateY(-1px);
        }
        @media (max-width: 992px) {
          .bms-hero {
            flex-direction: column;
            align-items: stretch;
          }
          .bms-hero-stats {
            width: 100%;
            justify-content: space-between;
          }
          .bms-hero-stat {
            flex: 1;
            min-width: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default BoardMediumSettings;
